// FIXED RATIO - ACTIVE
import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { LDRecord } from '@launchdarkly/session-replay';
import 'react-toastify/dist/ReactToastify.css';

// HOOKS
import { useRefreshToken } from '../hooks/useTokenManager';
// APIs
import { useGetUserDataQuery } from '../apis/rtkData.api';
import {
  fetchChapterDataApi,
  fetchLearningPathLevelApi,
} from '../apis/data.api';
// Images
import Logo from '../images/lavenir-logo.png';
// REDUX
import {
  setCurrentPage,
  unloadUnityApp,
  setUnityAppRunning,
  setSentTokenMessage,
} from '../redux/app.slice';
// PROCESSES
import { procTraineeUnityData } from '../processes/fetchUserDataRtkQ';

const UnityApp = () => {
  const dispatch = useDispatch();
  const iframeRef = useRef();
  const [pageInitialLoaded, setPageInitialLoaded] = useState(false);

  const [isPlaying, setIsPlaying] = useState(true);
  const [sentMessage, setSentMessage] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false); // eslint-disable-line
  const [userName, setUserName] = useState(); // eslint-disable-line
  const [userLoggedIn, setUserLoggedIn] = useState(false);

  const [chapters, setChapters] = useState([]);
  const [startDate, setStartDate] = useState(new Date('07/01/23'));
  const [endDate, setEndDate] = useState(new Date());
  const [userData, setUserData] = useState({});
  const [userProgressDataReady, setUserProgressDataReady] = useState(false);
  const [trainingProgData, setTrainingProgData] = useState(null);

  const [avatarProvider, setAvatarProvider] = useState(null);
  const [learningPathLevel, setLearningPathLevel] = useState('default');
  const [isLearningPathEnabled, setIsLearningPathEnabled] = useState(false);

  const [score, setScore] = useState(); // eslint-disable-line
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user); // eslint-disable-line
  const app = useSelector((state) => state.app); // eslint-disable-line
  const { refreshCount } = useSelector((state) => state.user); // eslint-disable-line
  const { idToken, expiresAt, isRefreshing } = useSelector((state) => state.token);
  const { reactVerbosity } = useSelector((state) => state.app); // eslint-disable-line

  useRefreshToken();

  const selection = {
    startDate,
    endDate,
  };

  const {
    data: usrData,
    isLoading: isLoadingUsrData,
    refetch: refetchUserData, // eslint-disable-line
  } = useGetUserDataQuery({
    authToken: idToken,
    user: user,
    selection,
  });

  // NOTE: canvas sizing is handled entirely by CSS (the unity-* grid classes,
  // which are viewport-relative) together with `matchWebGLToCanvasSize: true`
  // below. The old manual `resizeCanvas` handler that used to live here threw on
  // every resize (it referenced the imported `Unity` component, which has no
  // `.style`), so it never actually sized anything — its removal is a no-op for
  // layout and eliminates the "An error occurred running the Unity content" alert.

  function handleCacheControl() {
    // Allow caching but force revalidation - this enables smart caching
    return 'public, max-age=2592000, must-revalidate';
  }

  // Generate version-based cache parameter (only version, no timestamp)
  const getCacheVersion = () => {
    const buildVersion = process.env.REACT_APP_WEBGL_BUILD_ID || '1.0.0';
    return `v=${buildVersion}`;
  };

  const {
    unityProvider,
    isLoaded,
    loadingProgression,
    sendMessage,
    addEventListener, // eslint-disable-line
    removeEventListener, // eslint-disable-line
    requestFullscreen,
    takeScreenshot, // eslint-disable-line
    unload,
  } = (() => {
    const cacheVersion = getCacheVersion();
    const buildId = process.env.REACT_APP_WEBGL_BUILD_ID;
    const useBrotli = process.env.REACT_APP_USE_BROTLI !== 'false';

    const base = `${buildId}/Build/${buildId}`;
    const loaderUrl = `${base}.loader.js?${cacheVersion}`;
    const dataUrl = useBrotli
      ? `${base}.data.br?${cacheVersion}`
      : `${base}.data?${cacheVersion}`;
    const frameworkUrl = useBrotli
      ? `${base}.framework.js.br?${cacheVersion}`
      : `${base}.framework.js?${cacheVersion}`;
    const codeUrl = useBrotli
      ? `${base}.wasm.br?${cacheVersion}`
      : `${base}.wasm?${cacheVersion}`;

    return useUnityContext({
      loaderUrl,
      dataUrl,
      frameworkUrl,
      codeUrl,
      matchWebGLToCanvasSize: true,
      cacheControl: handleCacheControl,
      companyName: 'LavenirAI',
      productName: 'LavenirAI',
      productVersion: '1b.0.3',
      webglContextAttributes: {
        preserveDrawingBuffer: true,
      },
    });
  })();

  // Smart cache management - only clear if version changed
  const checkAndClearOldCache = async () => {
    try {
      const currentVersion = process.env.REACT_APP_WEBGL_BUILD_ID || '1.0.0';
      const storedVersion = localStorage.getItem('unity_webgl_build_id');

      if (storedVersion && storedVersion !== currentVersion) {
        console.log('[UnityApp] WebGL version changed, clearing old cache');

        if ('caches' in window) {
          const cacheNames = await caches.keys();
          const unityCaches = cacheNames.filter(
            (name) =>
              name.includes('unity') ||
              name.includes('webgl') ||
              name.includes(process.env.REACT_APP_WEBGL_BUILD_ID)
          );

          await Promise.all(unityCaches.map((cacheName) => caches.delete(cacheName)));

          console.log('[UnityApp] Old Unity WebGL caches cleared:', unityCaches);
        }
      }

      localStorage.setItem('unity_webgl_build_id', currentVersion);
    } catch (error) {
      console.warn('[UnityApp] Failed to check/clear Unity cache:', error);
    }
  };

  useEffect(() => {
    dispatch(setCurrentPage('unityApp'));
    dispatch(setSentTokenMessage(false));
    dispatch(unloadUnityApp(false));
    dispatch(setUnityAppRunning(true));
    setUserProgressDataReady(false);

    checkAndClearOldCache();
  }, []); // eslint-disable-line

  useEffect(() => {
    setUserLoggedIn(true);
  }, []); // eslint-disable-line

  // user.token
  useEffect(() => {
    reactVerbosity === 6 && console.log('!!! ---> REFRESHING UNITY APP TOKEN USER', user);
    if (user.name === '' || user.idToken === '') {
      console.log('[UnityApp] User not found in Redux, redirecting to login');
      setIsPlaying(false);
      dispatch(setUnityAppRunning(false));
      window.location.href = '/login';
    }
    if (idToken && !isRefreshing) {
      reactVerbosity === 6 &&
        console.log(
          '$$$$ --- useTokenManager: checkTokenExpiration idToken VS user.idToken:',
          idToken !== user.idToken ? 'DIFFERENT TOKEN' : 'SAME TOKEN'
        );
      if (idToken === user.idToken) {
        reactVerbosity === 6 && console.log('!!! ---> sendMessage NEW UNITY APP TOKEN');
        sendMessage('UserSessionContext', 'ReceiveNewAuthToken', idToken);
      }
    }
  }, [idToken, isRefreshing, refreshCount, user.idToken]); // eslint-disable-line

  useEffect(() => {
    reactVerbosity === 6 && console.log('--- UnityApp usrData (verb6):', usrData);

    if (!isLoadingUsrData && usrData && usrData.error && usrData.error.message.includes('token')) {
      reactVerbosity === 6 && console.log('UNITY APP !!! ---> REFRESHING UNITY APP TOKEN');
      useRefreshToken();
    } else {
      if (!isLoadingUsrData && usrData) {
        fetchUserDataProcess();
      }
    }
  }, [isLoadingUsrData, usrData]); // eslint-disable-line

  useEffect(() => {
    if (pageInitialLoaded && app.unloadUnityApp) {
      reactVerbosity === 6 && console.log('!!! UnityApp UNLOADING APP !!!');
      handleUnload();
      dispatch(setUnityAppRunning(false));
      dispatch(setSentTokenMessage(false));
      setIsPlaying(false);
      setTimeout(() => {
        window.location.href = '/';
      }, 50);
    }
  }, [pageInitialLoaded, app.unloadUnityApp]); // eslint-disable-line

  useEffect(() => {
    reactVerbosity === 6 &&
      console.log('!!!!!! UnityApp idToken:', idToken != null ? 'not null' : 'null');
    if (
      idToken != null &&
      loadingProgression >= 1 &&
      isLoaded &&
      userProgressDataReady &&
      trainingProgData != null &&
      avatarProvider !== null
    ) {
      setTimeout(() => {
        reactVerbosity === 6 && console.log('==============> LOADED SEND TOKEN!!!!');
        handleClickSendToken();
        setSentMessage(true);
        dispatch(setSentTokenMessage(true));
        setPageInitialLoaded(true);
      }, 300);
    }
  }, [
    loadingProgression,
    isLoaded,
    userProgressDataReady,
    trainingProgData,
    avatarProvider,
  ]); // eslint-disable-line

  const ldClient = useLDClient();
  useEffect(() => {
    if (!isLoaded || !ldClient) return;
    const canvas = document.querySelector('canvas');
    if (!canvas) return;
    const fps = Number(process.env.REACT_APP_LD_CANVAS_FPS) || 8;
    const intervalMs = 1000 / fps;
    const intervalId = setInterval(() => {
      try {
        LDRecord.snapshot(canvas);
      } catch (err) {
        // LD auth failed or SDK in bad state — swallow so the interval
        // doesn't propagate errors. Replay just won't capture this frame.
      }
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [isLoaded, ldClient]);

  useEffect(() => {
    window.addEventListener('popstate', (e) => {
      navigate('/app', { replace: false });
    });
    return async () => {};
  }, []); // eslint-disable-line

  async function handleUnload() {
    sendMessage('UserSessionContext', 'EndSession');
    setTimeout(async () => {
      await unload();
    }, 1000);
  }

  useEffect(() => {
    if (Object.keys(userData).length > 0) {
      const result = buildDataForTrainingProgressTable();
      setTimeout(() => {
        setUserProgressDataReady(result);
      }, 1500);
    }
  }, [userData && userData.sessionCount]); // eslint-disable-line

  const fetchUserDataProcess = async () => {
    try {
      const chapterData = await fetchChapterDataApi(idToken, user);
      const { userDataObj } = await procTraineeUnityData(usrData);

      // --- chapters handling ---
      let chaptersFromResponse = [];
      if (Array.isArray(chapterData?.data)) {
        chaptersFromResponse = chapterData.data;
      } else if (Array.isArray(chapterData?.chapters)) {
        chaptersFromResponse = chapterData.chapters;
      } else if (Array.isArray(chapterData?.data?.chapters)) {
        chaptersFromResponse = chapterData.data.chapters;
      }

      setChapters(chaptersFromResponse);
      setUserData(userDataObj);

      // Learning path level from Users collection
      let resolvedLearningPath = 'default';
      let learningPathEnabled = false;
      try {
        const lpResp = await fetchLearningPathLevelApi(idToken, user);
        resolvedLearningPath = lpResp?.learningPathLevel || 'default';
        learningPathEnabled = lpResp?.learningPathEnabled || false;
        console.log('[UnityApp] lpResp:', lpResp);
        console.log('[UnityApp] resolvedLearningPath:', resolvedLearningPath);
        console.log('[UnityApp] Learning path enabled or not?:', learningPathEnabled ? 'enabled' : 'disabled');
      } catch (err) {
        console.error(
          '[UnityApp] Failed to fetch learning path level, defaulting to "default"',
          err
        );
      }
      setLearningPathLevel(resolvedLearningPath);
      setIsLearningPathEnabled(learningPathEnabled);
      console.log('[UnityApp] Resolved learningPathLevel:', resolvedLearningPath);

      let envProvider = process.env.REACT_APP_NEGOTIATION_AVATAR_PROVIDER;
      if (envProvider && envProvider.trim().toLowerCase() === 'heygen') {
        envProvider = 'HeyGen';
      } else {
        envProvider = 'Unity';
      }
      setAvatarProvider(envProvider);
      console.log('[UnityApp] Avatar provider resolved from ENV:', envProvider);
    } catch (err) {
      console.error('[UnityApp] fetchUserDataProcess failed:', err);
    }
  };

  const buildDataForTrainingProgressTable = async () => {
    reactVerbosity === 6 && console.log('--- UnityApp USER DATA (build table):', userData);

    const lessons = {};
    const quizzes = {};
    const negotiations = {};
    const selfAssessments = {};

    const tableData =
      (userData.sessions &&
        userData.sessions.map((s) => {
          return s.unitysessionmodules.map((m) => {
            if (m.moduleType === 'lesson' && m.userScore !== -1) {
              let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
              const dateArr = date.split('-');
              date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];

              if (!lessons[s.user.name]) lessons[s.user.name] = {};
              if (!lessons[s.user.name][m.moduleName]) {
                lessons[s.user.name][m.moduleName] = {};
              }
              lessons[s.user.name][m.moduleName] = {
                moduleType: 'Lesson',
                module: m.moduleName,
                user: s.user.name,
                userScore: m.userScore,
                complete: m.userScore === -1 ? 'X' : '\u2713',
                date,
              };
            }
            if (m.moduleType === 'quiz' && m.userScore !== -1) {
              let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
              const dateArr = date.split('-');
              date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];

              if (!quizzes[s.user.name]) quizzes[s.user.name] = {};
              if (!quizzes[s.user.name][m.moduleName]) {
                quizzes[s.user.name][m.moduleName] = {};
              }
              quizzes[s.user.name][m.moduleName] = {
                moduleType: 'Quiz',
                module: m.moduleName,
                user: s.user.name,
                userScore: m.userScore,
                complete: m.userScore === -1 ? 'X' : '\u2713',
                date,
              };
            }
            if (m.moduleType === 'negotiation' && m.userScore !== -1) {
              let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
              const dateArr = date.split('-');
              date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];

              if (!negotiations[s.user.name]) negotiations[s.user.name] = {};
              if (!negotiations[s.user.name][m.moduleName]) {
                negotiations[s.user.name][m.moduleName] = {};
              }
              negotiations[s.user.name][m.moduleName] = {
                moduleType: 'Negotiation',
                module: m.moduleName,
                user: s.user.name,
                userScore: m.userScore,
                complete: m.userScore === -1 ? 'X' : '\u2713',
                date,
              };
            }
            if (m.moduleType === 'selfassessment' && m.userScore !== -1) {
              let date = new Date(s.sessionStartTime).toISOString().split('T')[0];
              const dateArr = date.split('-');
              date = dateArr[1] + '-' + dateArr[2] + '-' + dateArr[0];

              if (!selfAssessments[s.user.name]) selfAssessments[s.user.name] = {};
              if (!selfAssessments[s.user.name][m.moduleName]) {
                selfAssessments[s.user.name][m.moduleName] = {};
              }
              selfAssessments[s.user.name][m.moduleName] = {
                moduleType: 'selfassessment',
                module: m.moduleName,
                user: s.user.name,
                userScore: m.userScore,
                complete: m.userScore === -1 ? 'X' : '\u2713',
                date,
              };
            }
          });
        })) ||
      [];

    let chapterLessons = [];
    let chapterQuizzes = [];
    let chapterNegotiations = [];
    let chapterSelfAssessments = [];
    let chapterLookup = {};

    chapters &&
      chapters.forEach((c) => {
        c.lessons.forEach((l) => {
          chapterLookup[l.moduleName] = c.title;
          chapterLessons = [
            ...chapterLessons,
            {
              chapter: c.title,
              chapterId: c._id,
              chapterIndex: c.chapterIndex,
              number: l.number,
              moduleType: 'Lesson',
              module: l.title,
              userScore: -1,
              complete: 'X',
              date: '',
            },
          ];
        });
        c.quizzes.forEach((q) => {
          chapterLookup[q.moduleName] = c.title;
          chapterQuizzes = [
            ...chapterQuizzes,
            {
              chapter: c.title,
              chapterId: c._id,
              chapterIndex: c.chapterIndex,
              number: q.number,
              moduleType: 'Quiz',
              module: q.title,
              userScore: -1,
              complete: 'X',
              date: '',
            },
          ];
        });
        c.negotiations.forEach((n) => {
          chapterLookup[n.moduleName] = c.title;
          chapterNegotiations = [
            ...chapterNegotiations,
            {
              chapter: c.title,
              chapterId: c._id,
              chapterIndex: c.chapterIndex,
              number: n.number,
              moduleType: 'Negotiation',
              module: n.title,
              userScore: -1,
              complete: 'X',
              date: '',
            },
          ];
        });
        c.selfassessments.forEach((n) => {
          chapterLookup[n.moduleName] = c.title;
          chapterSelfAssessments = [
            ...chapterSelfAssessments,
            {
              chapter: c.title,
              chapterId: c._id,
              chapterIndex: c.chapterIndex,
              number: n.moduleIndex,
              moduleType: 'selfassessment',
              module: n.moduleName,
              userScore: -1,
              complete: 'X',
              date: '',
            },
          ];
        });
      });

    const rebuiltTableData = [];
    const lessonKeys = Object.keys(lessons);
    lessonKeys.forEach((k) => {
      chapterLessons.forEach((l) => {
        l.user = k;
        if (!lessons[k][l.module]) {
          lessons[k][l.module] = { ...l };
        } else {
          lessons[k][l.module].chapter = l.chapter;
          lessons[k][l.module].number = l.number;
        }
        rebuiltTableData.push(lessons[k][l.module]);
      });
    });
    const quizKeys = Object.keys(quizzes);
    quizKeys.forEach((k) => {
      chapterQuizzes.forEach((q) => {
        q.user = k;
        if (!quizzes[k][q.module]) {
          quizzes[k][q.module] = { ...q };
        } else {
          quizzes[k][q.module].chapter = q.chapter;
          quizzes[k][q.module].number = q.number;
        }
        rebuiltTableData.push(quizzes[k][q.module]);
      });
    });
    const negotiationKeys = Object.keys(negotiations);
    negotiationKeys.forEach((k) => {
      chapterNegotiations.forEach((q) => {
        q.user = k;
        if (!negotiations[k][q.module]) {
          negotiations[k][q.module] = { ...q };
        } else {
          negotiations[k][q.module].chapter = q.chapter;
          negotiations[k][q.module].number = q.number;
        }
        rebuiltTableData.push(negotiations[k][q.module]);
      });
    });

    const selfAssessmentKeys = Object.keys(selfAssessments);
    selfAssessmentKeys.forEach((k) => {
      chapterSelfAssessments.forEach((q) => {
        q.user = k;
        if (!selfAssessments[k][q.module]) {
          selfAssessments[k][q.module] = { ...q };
        } else {
          selfAssessments[k][q.module].chapter = q.chapter;
          selfAssessments[k][q.module].number = q.number;
        }
        rebuiltTableData.push(selfAssessments[k][q.module]);
      });
    });

    let concatenatedTableData = [];
    const lessonKeyArr = Object.keys(lessons);
    lessonKeyArr.forEach((k) => {
      if (k === user.name) {
        const userLessonsKeyArr = Object.keys(lessons[k]);
        userLessonsKeyArr.forEach((lk) => {
          if (lessons[k][lk] != null && lessons[k][lk].userScore > 0) {
            lessons[k][lk].chapter = chapterLookup[lessons[k][lk].module];
            concatenatedTableData = [...concatenatedTableData, lessons[k][lk]];
          }
        });
      }
    });

    const quizKeyArr = Object.keys(quizzes);
    quizKeyArr.forEach((k) => {
      if (k === user.name) {
        const userQuizzesKeyArr = Object.keys(quizzes[k]);
        userQuizzesKeyArr.forEach((lk) => {
          if (quizzes[k][lk] != null && quizzes[k][lk].userScore > 0) {
            quizzes[k][lk].chapter = chapterLookup[quizzes[k][lk].module];
            concatenatedTableData = [...concatenatedTableData, quizzes[k][lk]];
          }
        });
      }
    });

    const negotiationKeyArr = Object.keys(negotiations);
    negotiationKeyArr.forEach((k) => {
      if (k === user.name) {
        const userNegotaitionsKeyArr = Object.keys(negotiations[k]);
        userNegotaitionsKeyArr.forEach((lk) => {
          if (negotiations[k][lk] != null && negotiations[k][lk].userScore > 0) {
            negotiations[k][lk].chapter = chapterLookup[negotiations[k][lk].module];
            concatenatedTableData = [...concatenatedTableData, negotiations[k][lk]];
          }
        });
      }
    });

    const selfAssessmentKeyArr = Object.keys(selfAssessments);
    selfAssessmentKeyArr.forEach((k) => {
      if (k === user.name) {
        const userSelfAssessmentsKeyArr = Object.keys(selfAssessments[k]);
        userSelfAssessmentsKeyArr.forEach((lk) => {
          if (selfAssessments[k][lk] != null && selfAssessments[k][lk].userScore > 0) {
            selfAssessments[k][lk].chapter =
              chapterLookup[selfAssessments[k][lk].module];
            concatenatedTableData = [...concatenatedTableData, selfAssessments[k][lk]];
          }
        });
      }
    });

    reactVerbosity === 6 &&
      console.log('!!!!! ------------> concatenatedTableData:', concatenatedTableData);

    const userProgData = concatenatedTableData
      .flat()
      .filter((data) => data.user === user.name);
    await setTrainingProgData(userProgData);
    reactVerbosity === 6 &&
      console.log('!!!!! ------------> userProgData:', userProgData, userProgData.length);
    return true;
  };

  // ----------------------------- SEND MESSAGE ------------------------------
  function handleClickSendToken() {
    let organizationId =
      typeof user.organization === 'string'
        ? user.organization
        : user.organization.name;

    let sendObj = {
      authtoken: idToken,
      userEmail: user.email,
      serverIp:
        process.env.REACT_APP_USE_BROTLI === 'false'
          ? 'http://localhost:4040'
          : process.env.REACT_APP_API_SERVER_IP,
      organizationId,
      learningPathLevel,
      isLearningPathEnabled,
      provider: avatarProvider || 'Unity',
      debugVerbosity: 2,
      deployServer: process.env.REACT_APP_DEPLOY_SERVER,
    };

    if (process.env.REACT_APP_USE_BROTLI === 'false') {
      sendObj = {
        ...sendObj,
        nodeManIp: 'http://localhost:4030',
        speechIp: 'http://localhost:4050',
      };
    }

    // Dev prompt override (set on the landing page). Dev-exclusive AND admin-only:
    // only when the build has the flag enabled and the launching user is an admin.
    // Only attach when the edited prompt actually differs from the loaded original.
    // Session-scoped; the app uses it only for the chosen case study + avatar and
    // nothing is persisted.
    try {
      const isAdmin = user && user.role === 'admin';
      const raw =
        isAdmin && process.env.REACT_APP_ENABLE_PROMPT_OVERRIDE === 'true'
          ? localStorage.getItem('devPromptOverride')
          : null;
      if (raw) {
        const ov = JSON.parse(raw);
        if (ov && ov.edited && ov.edited !== ov.original && ov.caseStudy && ov.personality) {
          sendObj = {
            ...sendObj,
            caseStudyOverride: ov.caseStudy,
            personalityOverride: ov.personality,
            systemMessageOverride: ov.edited,
          };
          console.log('[UnityApp] Attaching dev prompt override for', ov.personality, 'in', ov.caseStudy);
        }
      }
    } catch (e) {
      console.warn('[UnityApp] Failed to read devPromptOverride:', e);
    }

    console.log('[UnityApp] Sending user context to Unity:', {
      organizationId,
      learningPathLevel,
      provider: sendObj.provider,
      isLearningPathEnabled,
    });

    sendMessage('UserSessionContext', 'ReceiveUserContext', JSON.stringify(sendObj));

    if (userProgressDataReady && trainingProgData !== null) {
      const progressData = {
        userProgressData: trainingProgData,
      };
      reactVerbosity === 6 &&
        console.log(
          '!!!!!! ###### %%%%%%% --- progressData:',
          new Date().toISOString(),
          progressData
        );
      setTimeout(() => {
        sendMessage(
          'UserSessionContext',
          'ReceiveUserProgressData',
          JSON.stringify(progressData)
        );
      }, 0);
    }
  }

  const handleFullScreen = async () => {
    await requestFullscreen(true);
  };

  const filterNull = (tableData) => {
    return tableData.map((e) => {
      return e.map((m) => {
        if (m !== null && m !== undefined) {
          return m;
        }
        return [];
      });
    });
  };

  return (
    <div className="page-grid">
      <div className="unity-page-container">
        <div className="unity-page">
          <div className="unity-page-grid-1">
            {!isLoaded && (
              <div className="unity-page-loading-logo">
                <img src={Logo} style={{ width: '200px' }} alt="lavenirai" />
              </div>
            )}
            {!isLoaded && (
              <div className="unity-page-loading-progress">
                {Math.round(loadingProgression * 100)}%
              </div>
            )}
            <div id="unityContainer" className="unity-page-sect-1-1" ref={iframeRef}>
              <div className="unity-section-1-grid-1">
                {userLoggedIn && (
                  <Unity
                    className="unity-app-1"
                    id="unity"
                    unityProvider={unityProvider}
                    tabIndex="-1"
                    devicePixelRatio={window.devicePixelRatio}
                    style={{
                      display: isLoaded ? 'flex' : 'none',
                      visibility: isLoaded ? 'visible' : 'hidden',
                    }}
                  />
                )}
              </div>
              {isGameOver === true && (
                <p>{`Game Over ${userName}! You've scored ${score} points.`}</p>
              )}
            </div>

            <div className="unity-page-sect-2">
              <div id="unity-loading-bar">
                <div id="unity-logo"></div>
                <div id="unity-progress-bar-empty">
                  <div id="unity-progress-bar-full"></div>
                </div>
              </div>
            </div>

            <div id="unity-warning"> </div>

            <div className="unity-page-sect-4">
              {true && (
                <button className="unity-app-unload-button" onClick={handleUnload}>
                  Unload
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnityApp;
